import { Box, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { formatDateWithTime } from "../../../utils/dateUtils";
import { renderFieldView } from "../../../components/fieldsViews/renderFieldView";
import type { TreatmentSession } from "../types";
import type { Note } from "../../../types/records.types";
import type { useNoteCategories } from "./useNoteCategories";
import { SESSION_STATUS_TO_EXCLUDE } from "../treatment-prescription/utils";
import { getChoiceLabel, type Choice } from "../../../utils/choiceUtils";

export const getAvailableSessions = (allSessions: Array<TreatmentSession>, notes: Array<Note>, currentSessionId?: number | null) => {
	const usedSessionIds = new Set(
		notes.map((note) => note.sessions).filter(Boolean)
	);
	if (currentSessionId) usedSessionIds.delete(currentSessionId);
	return allSessions.filter((session) =>
		!usedSessionIds.has(session.id) && !SESSION_STATUS_TO_EXCLUDE.includes(session.status)
	);
};

type NoteSessionSelectProps = {
	value: number | "" | null;
	availableSessions: Array<TreatmentSession>;
	onChange: (sessionId: number) => void;
};

export const renderSessionView = (sessions: Array<TreatmentSession>, sessionsField: any, statusChoices: Array<Choice> = []) => {
	if (!sessionsField) return undefined;
	return (noteItem: any) => {
		const sessionId = noteItem?.sessions;
		if (!sessionId) return null;
		const session = sessions.find((s) => s.id === sessionId);
		if (!session) return null;
		const dateLabel = formatDateWithTime(session.start_date);
		const label = session.service.name ? `Sessão - ${session.service.name} ${dateLabel}` : `Sessão ${dateLabel}`;
		const statusLabel = getChoiceLabel(statusChoices, session.status);
		return renderFieldView(sessionsField, { sessions: { start_date: label, id: session.id, status: statusLabel } }, "group");
	};
};

export const NoteSessionSelect = ({ value, availableSessions, onChange }: NoteSessionSelectProps) => {
	return (
		<FormControl fullWidth size="small" sx={{ mb: 2 }}>
			<InputLabel>Sessão</InputLabel>
			<Select
				value={value ?? ""}
				label="Sessão"
				onChange={(e) => onChange(e.target.value as number)}
			>
				{availableSessions.map((session) => {
					const dateLabel = formatDateWithTime(session.start_date);
					const label = session.service.name ? `Sessão - ${session.service.name} ${dateLabel}` : `Sessão ${dateLabel}`;
					return (
						<MenuItem key={session.id} value={session.id}>
							{label}
						</MenuItem>
					);
				})}
				<MenuItem value="">
					<em>Sem sessão</em>
				</MenuItem>
			</Select>
		</FormControl>
	);
};

type CategoryHeaderProps = {
	categoryState: ReturnType<typeof useNoteCategories>["categoryState"];
};

export const CategoryHeader = ({ categoryState }: CategoryHeaderProps) => (
	<Box sx={{ mb: 2 }}>
		<ToggleButtonGroup
			value={categoryState.value}
			exclusive
			onChange={(_, newCategory) => {
				if (newCategory !== null) categoryState.onChange(newCategory);
			}}
			color="secondary"
			fullWidth
			size="small"
			sx={{ backgroundColor: "white" }}
		>
			{categoryState.choices.map((choice) => (
				<ToggleButton key={choice.value} value={choice.value} sx={{ textTransform: "none" }}>
					{choice.text}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	</Box>
);

type CategorySelectProps = {
	categoryState: ReturnType<typeof useNoteCategories>["categoryState"];
};

export const CategorySelect = ({ categoryState }: CategorySelectProps) => (
	<FormControl fullWidth size="small" sx={{ mb: 2 }}>
		<InputLabel>Categoria</InputLabel>
		<Select value={categoryState.value} label="Categoria" disabled>
			{categoryState.choices.map((choice) => (
				<MenuItem key={choice.value} value={choice.value}>
					{choice.text}
				</MenuItem>
			))}
		</Select>
	</FormControl>
);

type TitleSelectProps = {
	titleState: ReturnType<typeof useNoteCategories>["titleState"];
	currentTitle: string | null;
};

export const TitleSelect = ({ titleState, currentTitle }: TitleSelectProps) => {
	const value = titleState.value ?? currentTitle ?? "";
	return (
		<FormControl fullWidth size="small" sx={{ mb: 2 }}>
			<InputLabel>Título</InputLabel>
			<Select
				value={value}
				label="Título"
				onChange={(e) => titleState.onChange(e.target.value as string)}
			>
				{titleState.choices.map((choice) => (
					<MenuItem key={choice.value} value={choice.value}>
						{choice.text}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};
